import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { appRoutes } from './app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HttpClient, provideHttpClient } from '@angular/common/http';
import { provideToastr } from 'ngx-toastr';
import * as Sentry from '@sentry/angular';
import { APP_TYPE, AppTypeService, EnvHelper, logger, TenantStore } from '@frontend/shared';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, map } from 'rxjs';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideMapboxGL } from 'ngx-mapbox-gl';

logger.setLogLevel((window as any).spaConfig?.log_level || 'warn');

function createTranslateLoader(prefixes: string[] = ['./assets/i18n/']) {
  return (http: HttpClient) => {
    return {
      getTranslation: (lang: string) =>
        forkJoin(
          prefixes.map((prefix) => {
            return new TranslateHttpLoader(http, prefix, '.json').getTranslation(lang);
          })
        ).pipe(
          map((translations) => {
            return translations.reduce((acc, translations2) => {
              return { ...acc, ...translations2 };
            }, {});
          })
        ),
    };
  };
}

// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
export function initialise(translate: TranslateService): () => Promise<[void]> {
  return async () => {
    // Supported languages
    translate.addLangs(['de', 'en']);
    // User german as default
    translate.setDefaultLang('de');

    // Set translation to browser language if we support it
    const browserLang = translate.getBrowserLang() ?? 'de';
    if (translate.langs.indexOf(browserLang) < 0) {
      translate.use('de');
    } else {
      translate.use(browserLang);
    }

    const translatePromise = new Promise<void>((resolve) => {
      translate.onLangChange.subscribe(() => {
        resolve();
      });
    });

    return Promise.all([translatePromise]);
  };
}

const environment = EnvHelper.getWebEnv();
Sentry.init({
  dsn: 'https://816917ca5522f0f282db9b9c04faeede@o4509212613869568.ingest.de.sentry.io/4509269808971856',
  environment,
  enabled: environment !== 'local',
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader([
            './assets/i18n/',
            './assets/i18n/shared/',
            './assets/i18n/settings/',
          ]),
          deps: [HttpClient],
        },
      })
    ),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideToastr({
      timeOut: 3000,
      positionClass: 'toast-bottom-full-width',
      preventDuplicates: true,
      closeButton: true,
      progressBar: true,
    }),
    // TODO: integrate sentry with wire api client
    provideHttpClient(),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    { provide: APP_TYPE, useValue: 'main' },
    {
      provide: APP_INITIALIZER,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      useFactory: () => () => {},
      deps: [Sentry.TraceService, AppTypeService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initialise,
      deps: [TranslateService],
      multi: true,
    },
    provideAppInitializer(async () => {
      await inject(TenantStore).init();
    }),
    provideMapboxGL({
      accessToken:
        'pk.eyJ1Ijoib3BlbnJlc2VhcmNoIiwiYSI6ImNrcDU2cXhhczFyMHIycG13eGQ1enl6ajgifQ.yIruwhwKAxueP8JJUHYrKQ',
    }),
  ],
};
